import { useEffect, useState } from 'react';

import Button, { IconButton } from '@components/button/Button';
import { TagInput } from '@components/index';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import useNotify from '@hooks/notify';
import Modal from '@layouts/modal/Modal';
import { getLocalStorageItem } from '@utils/storage';
import { Tag } from 'types/lead-management';

import {
  useCreateTagMutation,
  useUpdateTagMutation
} from '../queries/LeadManagementQuery';

interface LeadsAddTagModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddTags: (tags: Tag[]) => void;
  existingTags?: Tag[];
  isBulkEdit?: boolean;
  tagsList?: Tag[];
  existingSelectedTags: Tag[];
  onTagRenameSuccess?: () => void;
}

const LeadsAddTagModal = ({
  isOpen,
  onClose,
  onAddTags,
  existingTags,
  isBulkEdit,
  tagsList,
  existingSelectedTags,
  onTagRenameSuccess
}: LeadsAddTagModalProps) => {
  const [inputValue, setInputValue] = useState('');
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [tagOptions, setTagOptions] = useState<Tag[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const organizationId = getLocalStorageItem(
    LOCAL_STORAGE_KEYS.ORGANIZATION_ID
  );
  const { createErrorAlert, createSuccessAlert } = useNotify();

  const [createTags] = useCreateTagMutation();
  const [updateTag] = useUpdateTagMutation();

  const handleInputChange = (value: string) => {
    setInputValue(value);
  };

  const handleTagsChange = async (selectedTagsList: Tag[]) => {
    const newCustomTags = selectedTagsList.filter((tag: Tag) =>
      tag.id.startsWith('custom-')
    );
    const tagWithIds = selectedTagsList.filter(
      (tag: Tag) => !tag.id.startsWith('custom-')
    );
    let createdTag: Tag | null = null;

    if (newCustomTags.length > 0) {
      // Since only one tag will be created at a time, get the first tag
      const tagToCreate = newCustomTags[0];

      try {
        const response = await createTags({
          organizationId,
          name: tagToCreate.name?.toLowerCase() || '',
          color: tagToCreate.color || '#F2E1E9'
        });

        if ('data' in response) {
          createdTag = {
            id: response.data.id,
            name: response.data.name,
            color: response.data.color
          };

          setTagOptions([...tagOptions, createdTag]);
        } else {
          createErrorAlert(`Failed to create tag "${tagToCreate.name}"`);
        }
      } catch (error) {
        createErrorAlert(
          `Failed to create tag "${tagToCreate.name}". Please try again.`
        );
      }
    }

    let finalSelectedTags = [...tagWithIds];
    if (createdTag?.id) {
      finalSelectedTags = [...tagWithIds, createdTag];
    }

    setSelectedTags(finalSelectedTags);
    setInputValue('');
  };

  const handleUpdateTag = async (tagItem: Tag) => {
    if (!organizationId || !tagItem.id) return;

    try {
      const response = await updateTag({
        organizationId,
        id: tagItem.id,
        name: tagItem.name,
        color: tagItem.color || '#F2E1E9'
      });

      if ('data' in response && response.data) {
        setTagOptions(
          tagOptions.map((tag) => (tag.id === tagItem.id ? response.data : tag))
        );
        createSuccessAlert(`Tag "${response.data.name}" updated successfully`);
        const isSelectedTag = selectedTags.find((tag) => tag.id === tagItem.id);

        if (isSelectedTag) {
          setSelectedTags(
            selectedTags.map((tag) =>
              tag.id === tagItem.id ? response.data : tag
            )
          );
        }
        onTagRenameSuccess?.();
      } else {
        createErrorAlert(`Failed to update tag "${tagItem.name}"`);
      }
    } catch (error) {
      createErrorAlert(
        `Failed to update tag "${tagItem.name}". Please try again.`
      );
    }
  };

  const handleAddTags = async () => {
    setIsSaving(true);
    try {
      await onAddTags(selectedTags);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setSelectedTags([]);
    setInputValue('');
    onClose();
  };

  useEffect(() => {
    if (tagsList) {
      setTagOptions(tagsList);
    }
  }, [tagsList]);

  useEffect(() => {
    if (isOpen) {
      const tags = [...existingSelectedTags, ...selectedTags];
      setSelectedTags(tags);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isBulkEdit) {
      setSelectedTags(existingTags || []);
    }
  }, [existingTags, isBulkEdit]);

  return (
    <Modal
      isOpen={isOpen}
      onCancel={handleCancel}
      isCloseIconRequired={false}
      className="w-139 max-w-[90%]"
    >
      <div className="flex items-center justify-between border-b border-grey30 p-6 pb-3">
        <div className="text-xl font-medium text-content">Add Tags</div>
        <IconButton
          iconName="close"
          variant="transparent"
          handleClick={handleCancel}
          className="p-1"
          iconSize="24"
        />
      </div>

      <div className="px-6 py-3">
        <div className="mb-6">
          <div className="mb-1 block text-sm font-medium text-darkGrey">
            Tag
          </div>
          <TagInput
            value={inputValue}
            onChange={(val) => {
              handleInputChange(val.toLowerCase());
            }}
            onTagsChange={handleTagsChange}
            selectedTags={selectedTags}
            tagOptions={tagOptions}
            onUpdateTag={handleUpdateTag}
            selectorProps={{ positionFixed: true }}
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-4 border-t border-grey30 px-6 py-3">
        <Button
          variant="tertiary"
          label="Cancel"
          handleClick={handleCancel}
          className="h-11 w-30 text-sm font-medium"
        />
        <Button
          loading={isSaving}
          variant="primary"
          label="Add"
          handleClick={handleAddTags}
          className="h-11 w-30 text-sm font-medium"
        />
      </div>
    </Modal>
  );
};

export default LeadsAddTagModal;
